export interface QrShareSelection {
  qrCode: boolean;
  link: boolean;
}

export type ShareOutcome = 'shared' | 'cancelled' | 'unavailable';

const LINK_AND_QR_EXPLANATION =
  "Here's the link and QR code for this event. Guests use them to request and vote on songs with VoteBeats. Feel free to use them however you like in your flyers, posts and announcements.";
const LINK_ONLY_EXPLANATION =
  "Here's the link for this event. Guests use it to request and vote on songs with VoteBeats. Feel free to use it however you like in your flyers, posts and announcements.";
const QR_ONLY_EXPLANATION =
  "Here's the QR code for this event. Guests scan it to request and vote on songs with VoteBeats. Feel free to use it however you like in your flyers, posts and announcements.";

export function hasShareSelection(selection: QrShareSelection): boolean {
  return selection.qrCode || selection.link;
}

function explanationFor(selection: QrShareSelection): string {
  if (selection.qrCode && selection.link) return LINK_AND_QR_EXPLANATION;
  if (selection.link) return LINK_ONLY_EXPLANATION;
  return QR_ONLY_EXPLANATION;
}

export function buildShareMessage(input: {
  eventName: string;
  eventDateTime: string;
  eventLink: string;
  selection: QrShareSelection;
}): string {
  const { eventName, eventDateTime, eventLink, selection } = input;
  if (!hasShareSelection(selection)) throw new Error('Nothing selected to share');

  const lines = [eventName, eventDateTime, '', explanationFor(selection)];
  if (selection.link) lines.push('', eventLink);
  return lines.join('\n');
}

function isAbortError(error: unknown): boolean {
  return typeof error === 'object' && error !== null && 'name' in error && error.name === 'AbortError';
}

function handleShareError(error: unknown): ShareOutcome {
  if (isAbortError(error)) return 'cancelled';
  console.warn('[qrShare] Share failed:', error);
  return 'unavailable';
}

export function shareEventKit(payload: { title: string; text: string; file: File | null }): Promise<ShareOutcome> {
  if (typeof navigator.share !== 'function') return Promise.resolve('unavailable');

  const data: ShareData = { title: payload.title, text: payload.text };
  try {
    if (payload.file) {
      data.files = [payload.file];
      if (typeof navigator.canShare !== 'function' || !navigator.canShare(data)) {
        return Promise.resolve('unavailable');
      }
    }
    return navigator.share(data).then((): ShareOutcome => 'shared', handleShareError);
  } catch (error) {
    console.warn('[qrShare] Share failed:', error);
    return Promise.resolve('unavailable');
  }
}
